import { useState, useCallback, useEffect } from 'react';
import { ChatMessage, ChatContext, ChatConversation } from './types';
import {
  getFinanceAssistantConversation,
  getFinanceAssistantConversations,
  sendFinanceAssistantMessage,
} from '../../services/financeService';

const MAX_MESSAGES = 30;
const MAX_MESSAGE_LENGTH = 2000;
const LAST_CONVERSATION_STORAGE_KEY = 'fluxi-finance-ai-last-conversation-id';

const rememberConversation = (id: string) => {
  window.localStorage.setItem(LAST_CONVERSATION_STORAGE_KEY, id);
};

const forgetConversation = () => {
  window.localStorage.removeItem(LAST_CONVERSATION_STORAGE_KEY);
};

export function useChatService(context: ChatContext) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const items = await getFinanceAssistantConversations();
      setConversations(items);
      return items;
    } catch {
      setConversations([]);
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const hydrateLastConversation = async () => {
      const items = await loadConversations();
      if (cancelled || items.length === 0) return;

      const lastConversationId = window.localStorage.getItem(LAST_CONVERSATION_STORAGE_KEY);
      const preferredConversation =
        items.find((conversation) => conversation.id === lastConversationId) ?? items[0];

      if (!preferredConversation) return;

      setIsLoadingHistory(true);
      try {
        const detail = await getFinanceAssistantConversation(preferredConversation.id);
        if (cancelled) return;

        setConversationId(detail.conversation.id);
        setMessages(detail.messages.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          createdAt: message.createdAt,
        })));
        rememberConversation(detail.conversation.id);
      } catch {
        if (!cancelled) forgetConversation();
      } finally {
        if (!cancelled) setIsLoadingHistory(false);
      }
    };

    hydrateLastConversation();

    return () => {
      cancelled = true;
    };
  }, [loadConversations]);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      setError('Tu mensaje es muy largo. Acórtalo un poco.');
      return;
    }
    if (messages.length >= MAX_MESSAGES) {
      setError('Esta conversación ya es muy larga. Cierra el chat y abre uno nuevo.');
      return;
    }

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendFinanceAssistantMessage({
        conversation_id: conversationId,
        surface: context.surface,
        funnel_id: context.funnelId ?? context.funnel?.funnelId,
        period: context.period ?? context.funnel?.period,
        messages: newMessages,
        context,
      });
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message ?? 'No pude generar una respuesta en este momento.',
      };
      if (response.conversationId) {
        setConversationId(response.conversationId);
        rememberConversation(response.conversationId);
      }
      setMessages([...newMessages, assistantMessage]);
      loadConversations();
    } catch {
      setError('No pude conectar con el asistente de Finance. Inténtalo de nuevo en unos momentos.');
      setMessages(newMessages);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, messages, context, loadConversations]);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    forgetConversation();
  }, []);

  const selectConversation = useCallback(async (id: string) => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const detail = await getFinanceAssistantConversation(id);
      setConversationId(detail.conversation.id);
      rememberConversation(detail.conversation.id);
      setMessages(detail.messages.map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        createdAt: message.createdAt,
      })));
    } catch {
      setError('No pude abrir esa conversación.');
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  return {
    messages,
    conversations,
    conversationId,
    sendMessage,
    startNewConversation,
    selectConversation,
    isLoading,
    isLoadingHistory,
    error,
  };
}
